/* Low-priority, mobile-only Category enhancement. Native links remain fallbacks. */
(() => {
  if (customElements.get('gjl-category-browser')) return;

  customElements.define('gjl-category-browser', class extends HTMLElement {
    connectedCallback() {
      if (this.lifetime && !this.lifetime.signal.aborted) return;
      this.lifetime = new AbortController();
      this.editorUnloaded = false;
      const lifetime = this.lifetime;
      const ready = () => {
        if (!this.isConnected || lifetime.signal.aborted) return;
        this.dialog = this.querySelector('dialog.gjl-browse');
        if (!this.dialog || typeof this.dialog.showModal !== 'function') return;
        if (!this.dialog.id || document.querySelectorAll(`#${CSS.escape(this.dialog.id)}`).length !== 1) return;
        this.historyKey ||= `__gjlCategoryBrowser_${crypto.randomUUID()}`;
        this.entries ||= new Map();
        if (this.dialog.open) this.dialog.close();
        const options = { signal: lifetime.signal };
        window.addEventListener('popstate', () => this.onPopState(), options);
        window.addEventListener('pagehide', () => this.suspend(), options);
        window.addEventListener('pageshow', () => this.resume(), options);
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.suspend();
          else this.resume();
        }, options);
        for (const type of ['shopify:section:unload', 'shopify:section:select']) {
          document.addEventListener(type, event => {
            const section = this.closest('[id^="shopify-section-"]');
            const id = event.detail?.sectionId;
            const relevant = id != null ? section?.id === `shopify-section-${id}` : event.target === section || event.target === this;
            if (!relevant) return;
            if (type === 'shopify:section:unload') this.editorUnloaded = true;
            this.suspend();
            if (type === 'shopify:section:select') this.resume();
          }, options);
        }
        this.resume();
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', ready, { once: true, signal: lifetime.signal });
      } else ready();
    }

    resume() {
      if (!this.isConnected || this.editorUnloaded || document.hidden || this.lifetime.signal.aborted) return;
      if (this.activeLifetime && !this.activeLifetime.signal.aborted) return;
      this.activeLifetime = new AbortController();
      const options = { signal: this.activeLifetime.signal };
      const navigationKey = window.navigation?.currentEntry?.key;
      if (this.pendingBack && navigationKey && navigationKey !== this.pendingBack.navigationKey) this.pendingBack = null;
      document.addEventListener('click', event => this.onClick(event), options);
      this.dialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.closePanel();
      }, options);
      this.dialog.addEventListener('close', () => {
        // Ignore a queued close event belonging to a panel already reopened.
        if (!this.dialog.open && this.showing) this.closePanel();
      }, options);
      this.dialog.addEventListener('keydown', event => this.onKey(event), options);
      this.syncAria(false);
    }

    syncAria(expanded) {
      this.ariaTargets ||= new Map();
      const attributes = { 'aria-haspopup': 'dialog', 'aria-controls': this.dialog.id, 'aria-expanded': String(expanded) };
      for (const trigger of document.querySelectorAll('a[data-gjl-category-trigger]')) {
        if (!this.ariaTargets.has(trigger)) {
          this.ariaTargets.set(trigger, Object.fromEntries(Object.keys(attributes).map(name => [name, { before: trigger.getAttribute(name) }])));
        }
        const record = this.ariaTargets.get(trigger);
        for (const [name, value] of Object.entries(attributes)) {
          const item = record[name];
          // If another owner changed an attribute, stop writing/restoring it.
          if (!item || ('written' in item && trigger.getAttribute(name) !== item.written)) {
            delete record[name];
            continue;
          }
          trigger.setAttribute(name, value);
          item.written = value;
        }
      }
    }

    restoreAria() {
      for (const [trigger, record] of this.ariaTargets || []) {
        for (const [name, item] of Object.entries(record)) {
          if (trigger.getAttribute(name) !== item.written) continue;
          if (item.before === null) trigger.removeAttribute(name);
          else trigger.setAttribute(name, item.before);
        }
      }
      this.ariaTargets?.clear();
    }

    suspend() {
      this.queuedOpener = null;
      cancelAnimationFrame(this.restoreFrame);
      this.closePanel(false, false);
      this.retireEntry();
      this.stopWatching();
      cancelAnimationFrame(this.focusFrame);
      this.activeLifetime?.abort();
      this.restoreAria();
    }

    disconnectedCallback() {
      this.suspend();
      this.lifetime?.abort();
      this.focusAnchor?.remove();
    }

    onClick(event) {
      if (event.target.closest?.('[data-gjl-browse-close]') && this.dialog.contains(event.target)) {
        this.closePanel();
        return;
      }
      const trigger = event.target.closest?.('a[data-gjl-category-trigger]');
      if (!trigger || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (this.dialog.open || !this.canOpen(trigger)) return;
      cancelAnimationFrame(this.focusFrame);
      this.opener = trigger;
      if (this.pendingBack) {
        this.queuedOpener = trigger;
        event.preventDefault();
        return;
      }
      if (this.openPanel()) event.preventDefault();
    }

    rendered(element) {
      if (!element?.isConnected) return false;
      for (let node = element; node; node = node.parentElement) {
        const style = getComputedStyle(node);
        if (node.hidden || style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0 || style.contentVisibility === 'hidden') return false;
        if (node.localName === 'details' && !node.open) {
          const summary = node.querySelector(':scope > summary');
          if (!summary?.contains(element)) return false;
        }
      }
      const rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0;
    }

    clickable(trigger) {
      if (!this.rendered(trigger) || trigger.closest('[inert], [aria-disabled="true"]')) return false;
      if (trigger.hasAttribute('download') || (trigger.target && trigger.target.toLowerCase() !== '_self')) return false;
      for (let node = trigger; node; node = node.parentElement) {
        if (getComputedStyle(node).pointerEvents === 'none') return false;
      }
      const rect = trigger.getBoundingClientRect();
      const x = rect.x + rect.width / 2;
      const y = rect.y + rect.height / 2;
      if (x < 0 || x >= innerWidth || y < 0 || y >= innerHeight) return false;
      const hit = document.elementFromPoint(x, y);
      return hit === trigger || trigger.contains(hit);
    }

    blocked() {
      if (this.closest('[inert]') || this.dialog.closest('[inert]')) return true;
      // An existing undecided gate is absolute, including its initial hidden phase.
      if (document.querySelector('#gyj-age-gate') && document.documentElement.dataset.gyjAge !== 'accepted') return true;
      const selectors = '#bndlr-discount-message, #gyj-age-gate, #shopify-pc__banner, #shopify-pc__prefs__dialog, #shopify-pc__prefs__overlay, dialog[open], [aria-modal="true"], cart-drawer.active, header details[open], shopify-chat[open]';
      return [...document.querySelectorAll(selectors)].some(element => {
        if (element === this.dialog || this.contains(element) || !this.rendered(element)) return false;
        const rect = element.getBoundingClientRect();
        return rect.bottom > 0 && rect.right > 0 && rect.top < innerHeight && rect.left < innerWidth;
      });
    }

    canOpen(trigger) {
      return this.isConnected && !this.activeLifetime?.signal.aborted && !document.hidden && matchMedia('(max-width: 749px)').matches && !this.blocked() && this.clickable(trigger);
    }

    currentEntry() {
      const entry = this.entries?.get(history.state?.[this.historyKey]);
      if (!entry || entry.url !== location.href) return null;
      // Navigation API IDs also detect native same-URL pushes/replacements that
      // copy our key. Do not traverse, rewrite, or adopt those foreign entries.
      if (entry.navigationId && entry.navigationId !== window.navigation?.currentEntry?.id) return null;
      return entry;
    }

    openPanel(entry) {
      cancelAnimationFrame(this.restoreFrame);
      const fresh = !entry;
      try {
        if (fresh) {
          const state = history.state;
          // A primitive/array/Date/Map root cannot be merged without changing its
          // semantics. Leave the real /collections link alone instead of wrapping it.
          if (state !== null && (typeof state !== 'object' || Object.getPrototypeOf(state) !== Object.prototype)) return false;
          entry = { token: crypto.randomUUID(), page: [scrollX, scrollY], base: state, url: location.href };
          history.pushState({ ...state, [this.historyKey]: entry.token }, '');
          entry.navigationKey = window.navigation?.currentEntry?.key;
          entry.navigationId = window.navigation?.currentEntry?.id;
          this.entries.set(entry.token, entry);
        }
        cancelAnimationFrame(this.focusFrame);
        this.pagePosition = entry.page;
        // Native close() restores its pre-modal focus. Store an OWNED anchor,
        // not the bottom nav, so yielding cannot implicitly steal native focus.
        // The host is display:contents in the production Liquid/CSS; it cannot
        // receive focus. A box-generating, non-tab-stop OWNED span can.
        if (!this.focusAnchor?.isConnected) {
          this.focusAnchor = document.createElement('span');
          this.focusAnchor.tabIndex = -1;
          this.focusAnchor.setAttribute('data-gjl-browse-focus-anchor', '');
          this.focusAnchor.style.cssText = 'position:fixed;width:1px;height:1px;top:0;left:0;opacity:0;pointer-events:none;overflow:hidden';
          this.append(this.focusAnchor);
        }
        this.focusAnchor.focus({ preventScroll: true });
        this.dialog.showModal();
        this.showing = true;
        this.syncAria(true);
        this.dialog.querySelector('[data-gjl-browse-close]')?.focus({ preventScroll: true });
        this.querySelector('[data-gjl-browse-scroll]').scrollTop = this.panelPosition || 0;
        this.startWatching();
        return true;
      } catch (_) {
        this.showing = false;
        this.stopWatching();
        if (this.dialog.open) this.dialog.close();
        if (fresh) this.retireEntry();
        if (document.activeElement === this.focusAnchor && this.canOpen(this.opener)) this.opener.focus({ preventScroll: true });
        return false;
      }
    }

    startWatching() {
      this.stopWatching();
      this.watchLifetime = new AbortController();
      const schedule = () => {
        if (!this.dialog.open) return;
        if (!this.checkFrame) this.checkFrame = requestAnimationFrame(() => {
          this.checkFrame = 0;
          this.checkPriority();
        });
        this.checkTimers?.forEach(clearTimeout);
        // Finite follow-ups cover fade-in after a zero-opacity mutation. No RAF loop.
        this.checkTimers = [50, 180, 450, 1000].map(delay => setTimeout(() => this.checkPriority(), delay));
      };
      this.observer = new MutationObserver(schedule);
      this.observer.observe(document.documentElement, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'open', 'aria-hidden', 'aria-modal', 'inert', 'id', 'data-gyj-age']
      });
      const options = { passive: true, signal: this.watchLifetime.signal };
      window.addEventListener('resize', schedule, options);
      window.visualViewport?.addEventListener('resize', schedule, options);
      for (const name of ['transitionrun', 'transitionend', 'animationstart', 'animationend', 'toggle']) {
        document.addEventListener(name, schedule, { ...options, capture: true });
      }
      schedule();
    }

    stopWatching() {
      this.watchLifetime?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.checkFrame);
      this.checkFrame = 0;
      this.checkTimers?.forEach(clearTimeout);
      this.checkTimers = [];
    }

    checkPriority() {
      if (this.dialog.open && (!matchMedia('(max-width: 749px)').matches || this.blocked())) {
        this.closePanel(false, false);
        this.retireEntry();
      }
    }

    retireEntry() {
      const entry = this.currentEntry();
      if (!entry) return;
      const state = { ...history.state };
      delete state[this.historyKey];
      try {
        history.replaceState(entry.base === null && !Object.keys(state).length ? null : state, '');
      } catch (_) { /* An inactive document may reject writes; never touch other entries. */ }
    }

    onPopState() {
      cancelAnimationFrame(this.restoreFrame);
      const entry = this.currentEntry();
      if (this.pendingBack && (this.pendingBack.navigationKey
        ? window.navigation?.currentEntry?.key === this.pendingBack.navigationKey
        : entry?.token === this.pendingBack.token)) return;
      this.pendingBack = null;
      const queued = this.queuedOpener;
      this.queuedOpener = null;
      if (this.activeLifetime?.signal.aborted) return;
      if (entry) {
        if (!this.dialog.open) {
          // Fast Back/Forward may beat the existing nav's un-stow observer.
          this.restoreFrame = requestAnimationFrame(() => {
            this.restoreFrame = requestAnimationFrame(() => {
              if (this.currentEntry() !== entry || this.dialog.open) return;
              if (this.canOpen(this.opener)) this.openPanel(entry);
              else this.retireEntry();
            });
          });
        }
      } else {
        this.closePanel(false);
        if (queued && this.canOpen(queued)) {
          this.opener = queued;
          this.openPanel();
        }
      }
    }

    closePanel(traverse = true, restoreFocus = true) {
      if (!this.dialog?.open && !this.showing) return;
      this.showing = false;
      this.stopWatching();
      cancelAnimationFrame(this.focusFrame);
      this.panelPosition = this.querySelector('[data-gjl-browse-scroll]').scrollTop;
      this.dialog.close();
      this.syncAria(false);
      if (restoreFocus && !this.blocked()) window.scrollTo(...this.pagePosition);
      if (traverse && this.currentEntry()) {
        this.pendingBack = this.currentEntry();
        history.back();
      }
      if (!restoreFocus) return;
      // The independent bottom-nav observer needs a frame to un-stow itself.
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = requestAnimationFrame(() => {
          if (!this.dialog.open && this.canOpen(this.opener)) this.opener.focus({ preventScroll: true });
        });
      });
    }

    onKey(event) {
      if (event.key !== 'Tab' || !this.dialog.open) return;
      const items = [...this.dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
        .filter(element => element.tabIndex >= 0 && !element.matches(':disabled') && !element.closest('[inert]') && this.rendered(element));
      const first = items[0];
      const last = items[items.length - 1];
      if ((!event.shiftKey && document.activeElement === last) || (event.shiftKey && document.activeElement === first)) {
        event.preventDefault();
        (event.shiftKey ? last : first)?.focus({ preventScroll: true });
      }
    }
  });
})();
